import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, CheckCircle2, Home, Building2, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Field, Area } from "@/components/forms/Fields";
import { submitLead, track } from "@/lib/api";
import useSeo from "@/lib/useSeo";

export default function Contact() {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", message: "", consent: false, website: "" });
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  useSeo({ title: "Contact | Emerald Crown Property Investments Nottinghamshire", description: "Get in touch with Emerald Crown Property Investments. Call, email or send us an enquiry about selling, agent opportunities or investment.", path: "/contact" });
  useEffect(() => { track("page_view", { page: "contact" }); }, []);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone) return toast.error("Please complete the required fields.");
    if (!form.consent) return toast.error("Please provide your consent to continue.");
    setBusy(true);
    try {
      await submitLead({ lead_type: "General Enquiry", full_name: form.full_name, email: form.email, phone: form.phone, notes: form.message, consent: form.consent, website: form.website });
      track("general_form_submit");
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="pt-20">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-24 grid grid-cols-1 lg:grid-cols-2 gap-14">
        <div>
          <span className="eyebrow">Contact</span>
          <h1 className="mt-4 text-4xl sm:text-5xl font-serif font-semibold text-slate-900 leading-tight">Let's talk</h1>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed">Whether you're a vendor, an estate agent, an investor or a professional adviser, we'd be glad to hear from you.</p>

          <div className="mt-8 space-y-3">
            <a href="tel:+447897674550" onClick={() => track("phone_click")} data-testid="contact-phone" className="flex items-center gap-3 bg-white rounded-xl border border-slate-200/80 p-4 hover:border-emerald-700 transition">
              <span className="grid place-items-center w-11 h-11 rounded-lg bg-emerald-50"><Phone className="w-5 h-5 text-emerald-800" /></span>
              <div><div className="text-xs text-slate-500">Call us</div><div className="font-medium text-slate-900">07897 674550</div></div>
            </a>
            <a href="mailto:hello@emeraldcrown.co.uk" onClick={() => track("email_click")} data-testid="contact-email" className="flex items-center gap-3 bg-white rounded-xl border border-slate-200/80 p-4 hover:border-emerald-700 transition">
              <span className="grid place-items-center w-11 h-11 rounded-lg bg-emerald-50"><Mail className="w-5 h-5 text-emerald-800" /></span>
              <div><div className="text-xs text-slate-500">Email us</div><div className="font-medium text-slate-900">hello@emeraldcrown.co.uk</div></div>
            </a>
          </div>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Link to="/sell" data-testid="contact-cta-seller" className="group rounded-xl border border-slate-200/80 bg-white p-4 hover:shadow-md transition"><Home className="w-5 h-5 text-emerald-800" /><div className="mt-2 font-medium text-slate-900 text-sm">Selling a property</div><span className="text-xs text-emerald-700 group-hover:underline">Sell your property →</span></Link>
            <Link to="/estate-agents" data-testid="contact-cta-agent" className="group rounded-xl border border-slate-200/80 bg-white p-4 hover:shadow-md transition"><Building2 className="w-5 h-5 text-emerald-800" /><div className="mt-2 font-medium text-slate-900 text-sm">Estate agent</div><span className="text-xs text-emerald-700 group-hover:underline">Send an opportunity →</span></Link>
            <Link to="/investment" data-testid="contact-cta-investor" className="group rounded-xl border border-slate-200/80 bg-white p-4 hover:shadow-md transition"><TrendingUp className="w-5 h-5 text-emerald-800" /><div className="mt-2 font-medium text-slate-900 text-sm">Investor</div><span className="text-xs text-emerald-700 group-hover:underline">Register interest →</span></Link>
          </div>
        </div>

        <div className="pb-24">
          {submitted ? (
            <div data-testid="contact-form-success" className="text-center bg-white rounded-2xl border border-slate-200/80 p-12">
              <CheckCircle2 className="w-14 h-14 text-emerald-700 mx-auto" />
              <h2 className="mt-5 text-2xl font-serif text-slate-900">Thanks for getting in touch.</h2>
              <p className="mt-3 text-slate-600">We've received your enquiry and will be in touch shortly.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white rounded-2xl border border-slate-200/80 p-6 sm:p-8 space-y-5" data-testid="contact-form">
              <h2 className="text-2xl font-serif text-slate-900">Send us a message</h2>
              <input type="text" name="website" value={form.website} onChange={(e) => set("website")(e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
              <Field label="Full name" required value={form.full_name} onChange={set("full_name")} testId="contact-name" />
              <Field label="Email" type="email" required value={form.email} onChange={set("email")} testId="contact-email-input" />
              <Field label="Phone" required value={form.phone} onChange={set("phone")} testId="contact-phone-input" />
              <Area label="Message" value={form.message} onChange={set("message")} rows={5} testId="contact-message" />
              <label className="flex items-start gap-3 text-sm text-slate-600">
                <input type="checkbox" checked={form.consent} onChange={(e) => set("consent")(e.target.checked)} className="mt-1 w-4 h-4 accent-emerald-800" data-testid="contact-consent" />
                <span>I consent to Emerald Crown Property Investments storing my details and contacting me in response to my enquiry. *</span>
              </label>
              <Button type="submit" disabled={busy} data-testid="contact-submit" className="bg-emerald-900 hover:bg-emerald-800 text-white rounded-full px-8 h-12 w-full">{busy ? "Sending…" : "Send message"}</Button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
